import { AgeGroupCategoryTypes, SegmentCategoryTypes, LeaderboardParamValues } from './constants';

/**
 * Combines singles and doubles rankings by subcategory
 * @param {Array} singlesRankings - Array of singles rankings
 * @param {Array} doublesRankings - Array of doubles rankings
 * @returns {Object} Combined rankings organized by subcategory
 */
export const combineRankings = (singlesRankings, doublesRankings) => {
    if (!singlesRankings?.length && !doublesRankings?.length) {
        return {};
    }

    const allSubcategories = new Set([
        ...(singlesRankings || []).map(r => r?.subcategory).filter(Boolean),
        ...(doublesRankings || []).map(r => r?.subcategory).filter(Boolean),
    ]);

    const result = {};
    Array.from(allSubcategories).forEach((subcategory) => {
        const singlesForSubcategory = (singlesRankings || []).filter(r => r?.subcategory === subcategory);
        const doublesForSubcategory = (doublesRankings || []).filter(r => r?.subcategory === subcategory);

        const categories = new Set([
            ...singlesForSubcategory.map(r => r?.category).filter(Boolean),
            ...doublesForSubcategory.map(r => r?.category).filter(Boolean),
        ]);

        const rankings = Array.from(categories).map((category) => {
            const singlesRanking = singlesForSubcategory.find(r => r?.category === category) || null;
            const doublesRanking = doublesForSubcategory.find(r => r?.category === category) || null;
            const baseRanking = singlesRanking || doublesRanking;

            const { wowChange, ...baseRankingWithoutWowChange } = baseRanking || {};

            return {
                ...baseRankingWithoutWowChange,
                singlesRank: singlesRanking?.rank || null,
                doublesRank: doublesRanking?.rank || null,
                singlesDisplayValue: singlesRanking?.displayValue || null,
                doublesDisplayValue: doublesRanking?.displayValue || null,
                singlesWowChange: singlesRanking?.wowChange ?? null,
                doublesWowChange: doublesRanking?.wowChange ?? null,
                isLocked: singlesRanking?.isLocked || doublesRanking?.isLocked || false,
            };
        }).sort((a, b) => a?.displayOrder - b?.displayOrder);

        const location = (singlesForSubcategory[0]?.location || doublesForSubcategory[0]?.location) || null;
        const locationKey = (singlesForSubcategory[0]?.locationKey || doublesForSubcategory[0]?.locationKey) || null;

        result[subcategory.toLowerCase()] = {
            location,
            locationKey,
            rankings,
        };
    });

    return result;
};

/**
 * Gets the age or segment value based on the category type
 * @param {string} category - The category type
 * @param {Object} rankingData - The ranking data object
 * @returns {string|null} The age group or segment value, or null if not applicable
 */
export const getAgeOrSegmentValue = (category, rankingData) => {
    if (!category || !rankingData) {
        return null;
    }

    if (Object.values(AgeGroupCategoryTypes).includes(category)) {
        return rankingData?.ageGroup;
    }
    if (Object.values(SegmentCategoryTypes).includes(category)) {
        return rankingData?.segment;
    }
    return null;
};

/**
 * Builds a rankings API URL with query parameters
 * @param {string} endpointPath - The API endpoint path
 * @param {string} ratingType - The rating type (e.g., 'Tennis')
 * @param {Object} rankingData - The ranking data object
 * @param {string|null} ageOrSegment - Optional age group or segment value
 * @returns {string} The complete URL with query parameters
 */
export const buildRankingsUrl = (endpointPath, ratingType, rankingData, ageOrSegment) => {
    const { category, gender, location, locationKey } = rankingData || {};
    const params = new URLSearchParams({
        RatingType: ratingType,
        CategoryType: category,
        Location: locationKey || location,
        Gender: gender,
    });

    if (ageOrSegment) {
        params.append(LeaderboardParamValues.AGE_OR_SEGMENT, ageOrSegment);
    }

    return `${endpointPath}?${params.toString()}`;
};
